import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Megaphone, Plus, Send, Trash2, FileText, Pencil, Users, Eye } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const emptyCamp = { name: "", channel: "whatsapp", message: "", filter: { target: "all", vehicle_brand: "", days_since_service: 90, min_outstanding: 0, min_loyalty_points: 100 } };
const emptyTpl  = { name: "", category: "promotion", channel: "whatsapp", message: "" };
const CATEGORIES = ["promotion", "service_reminder", "greeting", "offer", "other"];

export default function Campaigns() {
  const [tab, setTab] = useState("create");
  const [camp, setCamp] = useState(emptyCamp);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const pg = usePagination(history);
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(emptyTpl);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditId, setTplEditId] = useState(null);
  const [detailCamp, setDetailCamp] = useState(null);

  const loadHistory = () => api.get("/campaigns").then(r => setHistory(r.data)).catch(e => toast.error(formatErr(e)));
  const loadTpl = () => api.get("/campaign-templates").then(r => setTemplates(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { loadHistory(); loadTpl(); }, []);

  const doPreview = async () => {
    try {
      const { data } = await api.post("/campaigns/preview", camp);
      setPreview(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const doSend = async () => {
    if (!camp.name || !camp.message) { toast.error("Name & message required"); return; }
    try {
      const { data } = await api.post("/campaigns", camp);
      toast.success(`Campaign queued to ${data.reached_count} customer(s)`);
      setCamp(emptyCamp); setPreview(null); loadHistory(); setTab("history");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delCamp = async (id) => {
    if (!confirm("Delete this campaign?")) return;
    try { await api.delete(`/campaigns/${id}`); toast.success("Deleted"); loadHistory(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const saveTpl = async () => {
    try {
      if (tplEditId) await api.patch(`/campaign-templates/${tplEditId}`, tpl);
      else await api.post("/campaign-templates", tpl);
      toast.success(tplEditId ? "Updated" : "Template saved");
      setTpl(emptyTpl); setTplEditId(null); setTplOpen(false); loadTpl();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const editTpl = (t) => { setTpl(t); setTplEditId(t.id); setTplOpen(true); };
  const delTpl = async (id) => {
    if (!confirm("Delete this template?")) return;
    try { await api.delete(`/campaign-templates/${id}`); toast.success("Deleted"); loadTpl(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const applyTemplate = (t) => { setCamp(x => ({ ...x, channel: t.channel, message: t.message })); setTab("create"); toast.success("Template loaded"); };

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Megaphone className="w-3 h-3"/> CRM · Bulk WhatsApp & SMS outreach</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Marketing Campaigns</h1>
      </div>

      <div className="flex gap-1 mb-6 border-b border-border">
        {[["create", "Create Campaign", Send], ["history", "Campaign History", Users], ["templates", "Templates", FileText]].map(([k, l, I]) => (
          <button key={k} data-testid={`ctab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-1 transition-colors ${tab === k ? "text-primary border-b-2 border-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
            <I className="w-3 h-3"/> {l}
          </button>
        ))}
      </div>

      {tab === "create" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3 border border-border bg-card rounded-sm p-5">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Campaign Name</Label><Input data-testid="camp-name" value={camp.name} onChange={(e) => setCamp(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="Diwali Offer 2026"/></div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Channel</Label>
                <Select value={camp.channel} onValueChange={(v) => setCamp(x => ({ ...x, channel: v }))}>
                  <SelectTrigger data-testid="camp-channel" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Target Audience</Label>
              <Select value={camp.filter.target} onValueChange={(v) => setCamp(x => ({ ...x, filter: { ...x.filter, target: v } }))}>
                <SelectTrigger data-testid="camp-target" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  <SelectItem value="vehicle_brand">By vehicle brand</SelectItem>
                  <SelectItem value="last_service">Not serviced in X days</SelectItem>
                  <SelectItem value="outstanding">Has outstanding dues</SelectItem>
                  <SelectItem value="loyalty">Loyalty points ≥ X</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {camp.filter.target === "vehicle_brand" && (
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle Brand</Label><Input data-testid="camp-brand" value={camp.filter.vehicle_brand} onChange={(e) => setCamp(x => ({ ...x, filter: { ...x.filter, vehicle_brand: e.target.value } }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="Honda / Bajaj / TVS…"/></div>
            )}
            {camp.filter.target === "last_service" && (
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Days Since Last Service ≥</Label><Input data-testid="camp-days" type="number" value={camp.filter.days_since_service} onChange={(e) => setCamp(x => ({ ...x, filter: { ...x.filter, days_since_service: Number(e.target.value) } }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            )}
            {camp.filter.target === "outstanding" && (
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Min. Outstanding ₹</Label><Input data-testid="camp-outstanding" type="number" value={camp.filter.min_outstanding} onChange={(e) => setCamp(x => ({ ...x, filter: { ...x.filter, min_outstanding: Number(e.target.value) } }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            )}
            {camp.filter.target === "loyalty" && (
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Min. Loyalty Points</Label><Input data-testid="camp-loyalty" type="number" value={camp.filter.min_loyalty_points} onChange={(e) => setCamp(x => ({ ...x, filter: { ...x.filter, min_loyalty_points: Number(e.target.value) } }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Message</Label>
              <Textarea data-testid="camp-message" value={camp.message} onChange={(e) => setCamp(x => ({ ...x, message: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border font-mono" rows={6} placeholder="Hi {name}, get 20% off on brake pad replacement this month at SynqMoto ERP Workshop. Book now!"/>
              <div className="text-[10px] text-muted-foreground mt-1">{camp.message.length} chars</div>
            </div>

            <div className="flex gap-2">
              <Button data-testid="preview-btn" onClick={doPreview} variant="outline" className="rounded-sm text-xs uppercase tracking-wider"><Eye className="w-3 h-3 mr-1"/> Preview Audience</Button>
              <Button data-testid="send-camp-btn" onClick={doSend} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Send className="w-3 h-3 mr-1"/> Queue Send</Button>
            </div>
          </div>

          <div className="space-y-3">
            {preview && (
              <div className="border border-primary/40 bg-primary/5 rounded-sm p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary">Audience</div>
                <div className="font-display font-black text-3xl text-primary mt-1" data-testid="preview-count">{preview.count}</div>
                <div className="text-xs text-muted-foreground mt-2">customer(s) will receive this message</div>
                {preview.sample.length > 0 && (
                  <div className="mt-3 space-y-1 text-xs">
                    <div className="uppercase tracking-wider text-muted-foreground">Sample</div>
                    {preview.sample.map(c => <div key={c.id} className="text-muted-foreground">• {c.name} <span className="font-mono">({c.phone})</span></div>)}
                  </div>
                )}
              </div>
            )}
            {templates.length > 0 && (
              <div className="border border-border bg-card rounded-sm p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Quick Templates</div>
                <div className="space-y-1">
                  {templates.slice(0, 6).map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)} className="w-full text-left px-2 py-1 rounded-sm hover:bg-secondary text-xs">
                      <div className="font-semibold">{t.name}</div>
                      <div className="text-muted-foreground truncate">{t.message}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="border border-yellow-500/40 bg-yellow-500/5 rounded-sm p-3 text-xs text-muted-foreground">
              Delivery is queued — actual WhatsApp/SMS send needs Twilio configured. History records who would be reached.
            </div>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr><th className="text-left p-3">Campaign</th><th className="text-left p-3">Channel</th><th className="text-left p-3">Sent</th><th className="text-right p-3">Reach</th><th className="text-left p-3">Message</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {history.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No campaigns yet</td></tr>}
              {pg.pageItems.map(c => (
                <tr key={c.id} data-testid={`history-row-${c.id}`} className="border-t border-border">
                  <td className="p-3 font-semibold">{c.name}</td>
                  <td className="p-3 text-xs uppercase">{c.channel}</td>
                  <td className="p-3 font-mono text-xs">{(c.sent_at || "").slice(0, 10)}</td>
                  <td className="p-3 text-right font-mono text-primary">{c.reached_count}</td>
                  <td className="p-3 text-xs italic text-muted-foreground max-w-md truncate cursor-pointer" onClick={() => setDetailCamp(c)}>{c.message}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDetailCamp(c)} className="h-7 text-xs"><Eye className="w-3 h-3"/></Button>
                    <Button data-testid={`del-camp-${c.id}`} size="sm" variant="ghost" onClick={() => delCamp(c.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationBar pg={pg}/>
          <Dialog open={!!detailCamp} onOpenChange={(v) => !v && setDetailCamp(null)}>
            <DialogContent className="rounded-sm bg-card border-border max-w-lg">
              {detailCamp && <>
                <DialogHeader><DialogTitle className="font-display uppercase">{detailCamp.name}</DialogTitle></DialogHeader>
                <div className="space-y-2 text-sm">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Message ({detailCamp.channel.toUpperCase()})</div>
                  <div className="border border-border bg-secondary rounded-sm p-3 whitespace-pre-wrap font-mono text-xs">{detailCamp.message}</div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div><span className="text-muted-foreground">Reached: </span><span className="font-mono text-primary">{detailCamp.reached_count}</span></div>
                    <div><span className="text-muted-foreground">Sent: </span><span className="font-mono">{(detailCamp.sent_at || "").slice(0, 16)}</span></div>
                    <div><span className="text-muted-foreground">By: </span>{detailCamp.created_by}</div>
                    <div><span className="text-muted-foreground">Filter: </span><span className="uppercase">{detailCamp.filter?.target}</span></div>
                  </div>
                </div>
              </>}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === "templates" && (
        <div>
          <div className="flex justify-end mb-3">
            <Dialog open={tplOpen} onOpenChange={(v) => { setTplOpen(v); if (!v) { setTpl(emptyTpl); setTplEditId(null); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-tpl-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Plus className="w-4 h-4 mr-1"/> New Template</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm bg-card border-border max-w-lg">
                <DialogHeader><DialogTitle className="font-display uppercase">{tplEditId ? "Edit" : "New"} Template</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</Label><Input data-testid="tpl-name" value={tpl.name} onChange={(e) => setTpl(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
                      <Select value={tpl.category} onValueChange={(v) => setTpl(x => ({ ...x, category: v }))}>
                        <SelectTrigger data-testid="tpl-cat" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace("_", " ").toUpperCase()}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Channel</Label>
                      <Select value={tpl.channel} onValueChange={(v) => setTpl(x => ({ ...x, channel: v }))}>
                        <SelectTrigger className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="sms">SMS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Message</Label><Textarea data-testid="tpl-msg" value={tpl.message} onChange={(e) => setTpl(x => ({ ...x, message: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border font-mono" rows={5}/></div>
                  <Button data-testid="save-tpl-btn" onClick={saveTpl} disabled={!tpl.name || !tpl.message} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{tplEditId ? "Update" : "Save"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.length === 0 && <div className="col-span-2 border border-border bg-card p-8 rounded-sm text-center text-muted-foreground text-sm">No templates saved yet</div>}
            {templates.map(t => (
              <div key={t.id} data-testid={`tpl-${t.id}`} className="border border-border bg-card rounded-sm p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.category.replace("_", " ")} · {t.channel}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => applyTemplate(t)} className="h-7 text-xs text-primary"><Send className="w-3 h-3"/></Button>
                    <Button size="sm" variant="ghost" onClick={() => editTpl(t)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                    <Button data-testid={`del-tpl-${t.id}`} size="sm" variant="ghost" onClick={() => delTpl(t.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground whitespace-pre-wrap font-mono border-t border-border pt-2">{t.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
